'use client';

import logger from 'logger';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SignaturePad from 'signature_pad';
import { Box, Button, Color, styled, Typography } from 'ui';

const SignatureCanvas = styled('canvas')(({ theme }) => ({
  border: `1px solid ${theme.palette.primary.main}`,
  borderRadius: theme.spacing(2),
  height: '160px',
  width: '100%',
}));

const BLUE_INK_PEN = '#000F55';

const SIGNATURE_PAD_CONFIG = {
  maxWidth: 2,
  minWidth: 1,
  penColor: BLUE_INK_PEN,
};

interface Props {
  onSign: (base64PngSignature: string) => void;
  isFileLoaded: boolean;
  clearButtonColor?: Color;
  errorMessage?: string;
}

export const SignatureCollector = ({ onSign, isFileLoaded, clearButtonColor, errorMessage }: Props) => {
  const { t } = useTranslation('createContractWizard');
  const [isLoading, setIsLoading] = useState(false);
  const [isPadEmpty, setIsPadEmpty] = useState(true);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);

  useEffect(() => {
    if (!canvasRef.current || signaturePadRef.current) {
      return;
    }

    const ratio = Math.max(window.devicePixelRatio || 1, 1);

    canvasRef.current.width = canvasRef.current.offsetWidth * ratio;
    canvasRef.current.height = canvasRef.current.offsetHeight * ratio;
    canvasRef.current.getContext('2d')?.scale(ratio, ratio);

    signaturePadRef.current = new SignaturePad(canvasRef.current, SIGNATURE_PAD_CONFIG);

    const updatePadStatus = () => {
      if (signaturePadRef.current) {
        setIsPadEmpty(signaturePadRef.current.isEmpty());
      }
    };

    window.addEventListener('pointerup', updatePadStatus);
    window.addEventListener('touchend', updatePadStatus);
  }, [canvasRef, signaturePadRef]);

  const onSubmit = () => {
    setIsLoading(true);

    if (!signaturePadRef.current) {
      logger.debug('unable to submit signaturePadRef.current is null');
      setIsLoading(false);
      return;
    }

    try {
      const pngSignature = signaturePadRef.current.toDataURL();
      onSign(pngSignature);
    } catch (error) {
      logger.error('Error signing the document:', { error });
      setIsLoading(false);
    }
  };

  const onClear = () => {
    if (!signaturePadRef.current) {
      logger.debug('unable to clear signaturePadRef.current is null');
      return;
    }
    signaturePadRef.current.clear();
    setIsPadEmpty(true);
  };

  return (
    <Box flex={1} display="flex" flexDirection="column" justifyContent="space-between">
      <Box>
        <SignatureCanvas ref={canvasRef} data-testid="signature-canvas" />
      </Box>
      <Box>
        <Box display="flex" flexDirection="column" gap={2}>
          <Button
            size="lg"
            fullWidth
            onClick={onSubmit}
            loading={isLoading || !isFileLoaded}
            disabled={isPadEmpty}
            data-testid="add-signature-button"
            color="gradient"
          >
            {t('sign.handleSign')}
          </Button>
          {errorMessage && (
            <Typography variant="subtitle2" color="red.main">
              {errorMessage}
            </Typography>
          )}

          <Button
            size="lg"
            color={clearButtonColor || 'lightGrey'}
            fullWidth
            onClick={onClear}
            data-testid="clear-sign-button"
          >
            {t('sign.clear')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
